import { autoUpdate, computePosition, flip, size } from '@floating-ui/dom';
import { TmFormElement, watch } from '@thiememeulenhoff/lit-sdk';
import { html, PropertyValues } from 'lit';
import {
  customElement,
  property,
  query,
  queryAssignedElements,
} from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { tagName } from '../../helpers';
import { SelectOptionComponent } from './select-option/select-option.component';
import { styles } from './select.styles';

const tag = tagName('select');

@customElement(tag)
export class SelectComponent extends TmFormElement {
  static override styles = styles;

  @property({ type: Boolean })
  isOpen = false;

  @property({ type: Boolean })
  multiple = false;

  @property({ type: String })
  placement = 'bottom';

  @property({ type: Boolean })
  spacing = false;

  @query('.select-header')
  selectHeader!: HTMLElement;

  @query('.select-content')
  selectContent!: HTMLElement;

  cleanup?: () => void;

  @property({ type: String })
  valueContent = '';

  @property({ type: String })
  variant: 'default' | 'button' = 'default';

  @property({ type: String })
  searchValue = '';

  @property({ type: Boolean })
  isInputFocused = false;

  @property({ type: Boolean })
  filter = false;

  @query('#searchInput')
  searchInput?: HTMLInputElement;

  @queryAssignedElements({ selector: 'a-select-option' })
  options!: Array<SelectOptionComponent>;

  get placeholderContent(): string {
    return this.filter
      ? `<a-icon name="search" />${this.placeholder}`
      : this.placeholder || '';
  }

  override render() {
    return html`
      <div
        variant=${this.variant}
        data-test="select-header"
        class="select-header ${this.value ? '' : 'default-text'}"
        @click=${this.toggleSelect}
        @keydown=${this.handleKeyDown}
        tabindex=${this.disabled ? '-1' : '0'}
      >
        <div class="container">
          ${
            this.filter && this.isOpen
              ? html`<input
                  id="searchInput"
                  type="text"
                  data-test="search-input"
                  @input=${this.updateSearchValue}
                  @focus=${() => {
                    this.isInputFocused = true;
                  }}
                  @blur=${() => {
                    this.isInputFocused = false;
                  }}
                  aria-label="Search Input"
                />`
              : ''
          }
          <div
            class="${classMap({
              'input-focused': this.isInputFocused,
              hidden: this.searchValue !== '',
              'container-value': true,
            })}"
          >
            ${unsafeHTML(this.valueContent || this.placeholderContent)}
          </div>
        </div>
        <a-icon
          class="small"
          name="chevron-${this.isOpen ? 'up' : 'down'}"
        ></a-icon>
      </div>
      <div popover class="select-content" variant="${this.variant}">
        <slot></slot>
      </div>
    `;
  }

  override updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    if (changedProperties.has('isOpen') && this.isOpen) {
      this.searchInput?.focus();
    }
  }

  toggleSelect() {
    if (this.disabled) {
      return;
    }

    if (this.isOpen) {
      this.closeSelect();
    } else {
      this.openSelect();
    }
  }

  openSelect() {
    this.isOpen = true;
    this.filterOptions();
  }

  closeSelect() {
    this.isOpen = false;
    this.isInputFocused = false;
    this.resetSelect();
  }

  resetSelect() {
    this.searchValue = '';
    this.clearInputField();
  }

  clearInputField() {
    const inputField = this.shadowRoot?.getElementById(
      'searchInput',
    ) as HTMLInputElement;
    if (inputField) {
      inputField.value = '';
    }
  }

  updateSearchValue(event: InputEvent) {
    this.searchValue = (event.target as HTMLInputElement).value;
    this.filterOptions();
  }

  filterOptions() {
    this.options.forEach((option) => {
      const content = option.textContent?.toLowerCase() || '';
      if (content.includes(this.searchValue.toLowerCase())) {
        option.classList.remove('hidden');
      } else {
        option.classList.add('hidden');
      }
    });
  }

  handleOptionSelected(e: Event) {
    this.resetSelect();

    this.valueContent = '';

    if (this.multiple) {
      // Set as unknown and string to workaround IDE limitations
      this.value = this.options
        .filter((option) => option.selected)
        .map((option) => option.value) as unknown as string;
    } else {
      this.isOpen = false;
      this.value = (e as CustomEvent).detail.option;
      this.#setSelectedValues();
    }

    this.dispatchGlobalEvent({
      eventName: 'change',
      detail: {
        value: this.value,
        init: (e as CustomEvent).detail.init,
      },
    });
  }

  #setSelectedValues() {
    this.options.forEach((option) => {
      if (option.value === this.value) {
        option.setSelected(true);
      }

      if (!option.selected) {
        return;
      }

      if (option.value === this.value) {
        this.valueContent = option.getContent() ?? '';
        return;
      }

      if (option.selected) {
        option.setSelected(false);
      }
    });
  }

  handleKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      this.toggleSelect();
    }
  }

  @watch('value', { waitUntilFirstUpdate: true })
  handleValueChange() {
    if (!this.multiple) {
      this.#setSelectedValues();
    }
  }

  @watch('isOpen', { waitUntilFirstUpdate: true })
  handleOpenChange() {
    if (this.isOpen) {
      this.#addOpenListeners();
    } else {
      this.#removeOpenListeners();
    }

    this.selectContent.togglePopover(this.isOpen);
  }

  override firstUpdated(changedProperties: PropertyValues<this>) {
    super.firstUpdated(changedProperties);

    this.cleanup = autoUpdate(
      this.selectHeader,
      this.selectContent,
      this.updatePosition.bind(this),
    );

    if (this.multiple && this.value === '') {
      this.value = this.options.filter(
        (option) => option.selected,
      ) as unknown as string;
    }
  }

  updatePosition() {
    computePosition(this.selectHeader, this.selectContent, {
      placement: this.placement === 'top' ? 'top-start' : 'bottom-start',
      middleware: [
        size({
          apply: ({ availableHeight, elements }) => {
            this.style.setProperty(
              '--auto-size-available-height',
              `${availableHeight - 16}px`,
            );

            const reference = elements.reference as HTMLElement;

            this.style.setProperty(
              '--auto-size-available-width',
              `${reference.offsetWidth}px`,
            );
          },
        }),
        flip(),
      ],
    }).then(({ x, y }) => {
      Object.assign(this.selectContent.style, {
        'inset-inline-start': `${x}px`,
        'inset-block-start': `${this.spacing ? `${y - window.scrollY + 8}px` : `${y - window.scrollY}px`}`,
      });
    });
  }

  override connectedCallback() {
    super.connectedCallback();
    this.listen('option-selected', this.handleOptionSelected);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.cleanup?.();
  }

  #handleDocumentEvent = (event: Event) => {
    const path = event.composedPath();
    if (
      (this && !path.includes(this)) ||
      (event instanceof KeyboardEvent && event.key === 'Escape')
    ) {
      this.closeSelect();
    }
  };

  #addOpenListeners() {
    document.addEventListener('focusin', this.#handleDocumentEvent);
    document.addEventListener('mousedown', this.#handleDocumentEvent);
    document.addEventListener('keydown', this.#handleDocumentEvent);
  }

  #removeOpenListeners() {
    document.removeEventListener('focusin', this.#handleDocumentEvent);
    document.removeEventListener('mousedown', this.#handleDocumentEvent);
    document.removeEventListener('keydown', this.#handleDocumentEvent);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-select': SelectComponent;
  }

  interface HTMLElementEventMap {
    'option-selected': CustomEvent;
  }
}
